import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { toast } from "react-toastify";
import { useWarehouseTaskStore } from "../../../store/warehouseTask";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import api from "../../../service/api";
import {
  PackagePlus,
  UserCheck,
  CheckCircle,
  Box,
  XCircle,
  Play,
  RotateCcw,
  Hand,
  ArrowLeftRight,
  RefreshCw,
  Settings,
  MinusCircle,
  PackageSearch,
  Package,
  User,
  Calendar,
  ClipboardList,
  History,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Info,
  Truck,
  ArrowRight,
  ExternalLink,
  Trash2,
  AlertTriangle,
  Image as ImageIcon,
  UserMinus,
  Pause,
  Pencil,
  Phone,
  Check,
  Copy,
  Lock, Unlock,
  Scale,
  Boxes,
  Layers,
  Eye,
  PackageCheck,
  Undo2,
  Loader2,
  Clock,
} from "lucide-react";
import { OverlayPanel } from "primereact/overlaypanel";
import { InputTextarea } from "primereact/inputtextarea";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { WarehouseTask } from "../../../types/warehouse";
import { formatQuantity } from "../../../utils/format";
import { SmartNetworkImage } from "../../../components/SmartNetworkImage";
import { CargoStatusWidget } from "./components/CargoStatusWidget";
import { ImageLightbox } from "../../../components/ImageLightbox";
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  task: any;
}

export const ACTION_CONFIG: any = {
  // Kurulum & Atama
  CREATED: { label: "GÖREV OLUŞTURULDU", color: "blue", icon: PackagePlus },
  ASSIGNED: { label: "PERSONEL ATANDI", color: "indigo", icon: UserCheck },
  UNASSIGNED: {
    label: "PERSONEL ATAMASI KALDIRILDI",
    color: "slate",
    icon: UserMinus,
  }, // Yeni
  TRANSFERRED: {
    label: "GÖREV DEVREDİLDİ",
    color: "amber",
    icon: ArrowLeftRight,
  }, // Yeni

  // Süreç Kontrol
  PAUSED: { label: "GÖREV DURDURULDU", color: "orange", icon: Pause }, // Yeni
  RESUMED: { label: "GÖREV DEVAM EDİYOR", color: "sky", icon: Play }, // Yeni
  PRIORITY_CHANGED: {
    label: "ÖNCELİK GÜNCELLENDİ",
    color: "rose",
    icon: AlertCircle,
  }, // Yeni

  // Toplama Süreci
  PICKING_DONE: {
    label: "TOPLAMA TAMAMLANDI",
    color: "blue",
    icon: CheckCircle,
  },
  UNPICKED: {
    label: "TOPLAMA GERİ ALINDI",
    color: "orange",
    icon: MinusCircle,
  },

  // Paketleme Süreci
  PACKAGING_START: { label: "PAKETLEME BAŞLADI", color: "purple", icon: Play },
  PACKAGING_DONE: {
    label: "PAKETLEME TAMAMLANDI",
    color: "green",
    icon: CheckCircle,
  },
  UNPACKED: {
    label: "PAKETLEME GERİ ALINDI",
    color: "orange",
    icon: MinusCircle,
  },

  // Eksik Bildirimleri (Enum ile Tam Uyumlu)
  SHORTAGE_REPORTED: {
    label: "EKSİK BİLDİRİLDİ",
    color: "red",
    icon: AlertTriangle,
  },
  SHORTAGE_RESOLVED: {
    label: "EKSİK GİDERİLDİ",
    color: "emerald",
    icon: CheckCircle,
  },
  SHORTAGE_UNRESOLVED: {
    label: "EKSİK GİDERİLEMEDİ",
    color: "rose",
    icon: XCircle,
  }, // Yeni
  SHORTAGE_CANCELLED: {
    label: "EKSİK İPTAL EDİLDİ",
    color: "slate",
    icon: XCircle,
  }, // Yeni

  // Sistem
  ADMIN_UPDATE: { label: "ADMİN GÜNCELLEMESİ", color: "slate", icon: Settings },
};

// Yardımcı Fonksiyon
export const getActionStyle = (type: string) => {
  return (
    ACTION_CONFIG[type] || { label: type, color: "slate", icon: PackageSearch }
  );
};
export const WarehouseTaskDetailDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onDelete,
  task: initialTask,
}) => {
  const taskStore = useWarehouseTaskStore();
  const confirmModal = useConfirmModal();
  const [task, setTask] = React.useState(initialTask);
  const [tempNote, setTempNote] = React.useState(""); // Not düzenleme için
  const [copied, setCopied] = useState(false);
  const op = React.useRef<any>(null); // OverlayPanel için referans
  const apiBase = "https://api.plasiyo.com/";
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [galleryState, setGalleryState] = useState<{
    isOpen: boolean;
    images: string[];
    currentIndex: number;
    title?: string;
    sku?: string;
    defaultRotation?: number;
  }>({
    isOpen: false,
    images: [],
    currentIndex: 0,
    defaultRotation: 0,
  });
  const fetchedTaskId = React.useRef<number | null>(null);

  const [measurementDialog, setMeasurementDialog] = useState<{
    isOpen: boolean;
    taskItem: any;
    component: any;
    currentMeasurementId?: number;
    weight: number;
  }>({ isOpen: false, taskItem: null, component: null, weight: 0 });

  const [processingItemId, setProcessingItemId] = useState<{
    id: number;
    action: "pick" | "pack" | "unpick" | "unpack";
  } | null>(null);

  const [staffList, setStaffList] = useState<any[]>([]);

  useEffect(() => {
    taskStore.getStaffList().then((res: any) => {
      if (Array.isArray(res)) setStaffList(res);
    });
  }, []);

  const resolveUserName = useCallback((userId?: number | null, userObj?: { id?: number; name?: string } | null) => {
    if (userObj?.name && userObj.name.trim().length > 0) return userObj.name;
    if (!userId) return null;
    if (task?.assignedUser?.id === userId && task.assignedUser.name) return task.assignedUser.name;
    const action = task?.actions?.find((a: any) => a.userId === userId || a.user?.id === userId);
    if (action?.user?.name) return action.user.name;
    const staffMatch = staffList.find((s: any) => s.value === userId || s.id === userId);
    if (staffMatch?.label || staffMatch?.name) return staffMatch.label || staffMatch.name;
    return null;
  }, [task, staffList]);

  const onRefresh = async () => {
    if (task?.id) {
      const fullTask = await taskStore.getSingleTask(task.id, true);
      if (fullTask) setTask(fullTask);
    }
  };

  const handlePickItem = async (item: any) => {
    if (!task?.id) return;
    const remainingToPick = (item.quantity || 0) - (item.collectedQty || 0);
    if (remainingToPick <= 0) {
      toast.info("Bu ürün zaten tamamen toplanmış.");
      return;
    }
    const stockId = Number(item.stockId || item.stock?.id);
    const variantId = item.variantId ? Number(item.variantId) : undefined;

    setProcessingItemId({ id: item.id, action: "pick" });
    try {
      await taskStore.pickItem(task.id, {
        stockId,
        variantId,
        quantity: remainingToPick,
      });
      toast.success(`${item.stock?.name || "Ürün"} toplandı (${formatQuantity(remainingToPick)} adet).`);
      const updated = await taskStore.getSingleTask(task.id, true);
      if (updated) setTask(updated);
      taskStore.fetchDataDynamic(taskStore.queryParams);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Ürün toplanırken bir hata oluştu.");
    } finally {
      setProcessingItemId(null);
    }
  };

  const handlePackItem = async (item: any) => {
    if (!task?.id) return;
    const remainingToPack = (item.quantity || 0) - (item.packedQty || 0);
    if (remainingToPack <= 0) {
      toast.info("Bu ürün zaten tamamen paketlenmiş.");
      return;
    }

    const stockId = Number(item.stockId || item.stock?.id);
    const variantId = item.variantId ? Number(item.variantId) : undefined;

    setProcessingItemId({ id: item.id, action: "pack" });
    try {
      // Eğer toplanan miktar paketlenecek miktardan az ise, önce eksik kısmı topla sonra paketle
      const remainingToPick = (item.quantity || 0) - (item.collectedQty || 0);
      if (remainingToPick > 0) {
        await taskStore.pickItem(task.id, {
          stockId,
          variantId,
          quantity: remainingToPick,
        });
      }

      await taskStore.packItem(task.id, {
        stockId,
        variantId,
        quantity: remainingToPack,
      });

      toast.success(`${item.stock?.name || "Ürün"} paketlendi (${formatQuantity(remainingToPack)} adet).`);
      const updated = await taskStore.getSingleTask(task.id, true);
      if (updated) setTask(updated);
      taskStore.fetchDataDynamic(taskStore.queryParams);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Ürün paketlenirken bir hata oluştu.");
    } finally {
      setProcessingItemId(null);
    }
  };

  const handleUnpickItem = async (item: any) => {
    if (!task?.id) return;
    if ((item.collectedQty || 0) <= 0) return;

    const stockId = Number(item.stockId || item.stock?.id);
    const variantId = item.variantId ? Number(item.variantId) : undefined;

    setProcessingItemId({ id: item.id, action: "unpick" });
    try {
      if ((item.packedQty || 0) > 0) {
        await taskStore.unpackItem(task.id, {
          stockId,
          variantId,
          quantity: Number(item.packedQty),
        });
      }

      await taskStore.unpickItem(task.id, {
        stockId,
        variantId,
        quantity: Number(item.collectedQty),
      });

      toast.success(`${item.stock?.name || "Ürün"} toplama işlemi geri alındı.`);
      const updated = await taskStore.getSingleTask(task.id, true);
      if (updated) setTask(updated);
      taskStore.fetchDataDynamic(taskStore.queryParams);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "İşlem geri alınırken bir hata oluştu.");
    } finally {
      setProcessingItemId(null);
    }
  };

  const handleUnpackItem = async (item: any) => {
    if (!task?.id) return;
    if ((item.packedQty || 0) <= 0) return;

    const stockId = Number(item.stockId || item.stock?.id);
    const variantId = item.variantId ? Number(item.variantId) : undefined;

    setProcessingItemId({ id: item.id, action: "unpack" });
    try {
      await taskStore.unpackItem(task.id, {
        stockId,
        variantId,
        quantity: Number(item.packedQty),
      });

      toast.success(`${item.stock?.name || "Ürün"} paketleme işlemi geri alındı.`);
      const updated = await taskStore.getSingleTask(task.id, true);
      if (updated) setTask(updated);
      taskStore.fetchDataDynamic(taskStore.queryParams);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "İşlem geri alınırken bir hata oluştu.");
    } finally {
      setProcessingItemId(null);
    }
  };

  React.useEffect(() => {
    if (initialTask) {
      setTask(initialTask);
    }

    // Dialog açık, görev var, ama detayları (items) yoksa ve henüz çekilmediyse
    if (isOpen && initialTask?.id && !initialTask.items && fetchedTaskId.current !== initialTask.id) {
      fetchedTaskId.current = initialTask.id;
      taskStore.getSingleTask(initialTask.id, true)
        .then((fullTask) => {
          if (fullTask) setTask(fullTask);
        })
        .catch((err) => {
          console.error("Görev detayı getirilirken hata:", err);
          fetchedTaskId.current = null; // Hata alırsak tekrar deneyebilmesi için
        });
    }

    // Dialog kapanınca ref'i sıfırla ki tekrar açıldığında çekebilsin
    if (!isOpen) {
      fetchedTaskId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTask, isOpen]);
  const handleImageClick = (url: string) => {
    setSelectedImage(url);
  };
  const [editingTask, setEditingTask] = useState<WarehouseTask | null>(null);
  // ✅ HOOK SIRALAMASI DOĞRU: Önce tüm hooklar
  const stats = React.useMemo(() => {
    if (!task || !isOpen) return null;

    const physicalItems =
      task.items?.filter((item: any) => {
        const normalizedSku = item.stock?.sku?.toUpperCase().trim();
        return normalizedSku !== "K" && normalizedSku !== "BİB";
      }) || [];

    const totalQty = physicalItems.reduce(
      (acc: number, item: any) => acc + (Number(item.quantity) || 0),
      0,
    );
    const totalCollected = physicalItems.reduce(
      (acc: number, item: any) => acc + (Number(item.collectedQty) || 0),
      0,
    );
    const totalPacked = physicalItems.reduce(
      (acc: number, item: any) => acc + (Number(item.packedQty) || 0),
      0,
    );

    const pickingProgress = totalQty > 0 ? (totalCollected / totalQty) * 50 : 0;
    const packingProgress = totalQty > 0 ? (totalPacked / totalQty) * 50 : 0;
    const hybridProgress = Math.round(pickingProgress + packingProgress);

    const createdAction = task.actions?.find((a: any) => a.actionType === "CREATED") || task.actions?.find((a: any) => a.actionType === "ASSIGNED");
    const pickingStartAction = task.actions?.find((a: any) => a.actionType === "ASSIGNED") || createdAction;
    const pickingDoneAction = task.actions?.find((a: any) => a.actionType === "PICKING_DONE");
    const packingStartAction = task.actions?.find((a: any) => a.actionType === "PACKAGING_START") || pickingDoneAction;
    const packingDoneAction = task.actions?.find((a: any) => a.actionType === "PACKAGING_DONE");

    const toplayan = pickingDoneAction?.user?.name || "-";
    const paketleyen = packingDoneAction?.user?.name || "-";

    let pickingTimeMs = null;
    if (pickingStartAction && pickingDoneAction) {
      pickingTimeMs = new Date(pickingDoneAction.createdAt).getTime() - new Date(pickingStartAction.createdAt).getTime();
    }

    let packingTimeMs = null;
    if (packingStartAction && packingDoneAction) {
      packingTimeMs = new Date(packingDoneAction.createdAt).getTime() - new Date(packingStartAction.createdAt).getTime();
    }

    let totalTimeMs = null;
    const endAction = packingDoneAction || pickingDoneAction;
    if (createdAction && endAction) {
      totalTimeMs = new Date(endAction.createdAt).getTime() - new Date(createdAction.createdAt).getTime();
    }

    const getTheme = (progress: number) => {
      if (progress <= 0)
        return { color: "#94a3b8", label: "BAŞLANMADI", class: "bg-slate-400" };
      if (progress <= 50)
        return { color: "var(--primary, #2563eb)", label: "TOPLANIYOR", class: "bg-primary" };
      if (progress < 100)
        return {
          color: "var(--secondary, #8b5cf6)",
          label: "PAKETLENİYOR",
          class: "bg-secondary",
        };
      return { color: "var(--success, #22c55e)", label: "TAMAMLANDI", class: "bg-success" };
    };

    return {
      physicalItems,
      totalQty,
      totalCollected,
      totalPacked,
      hybridProgress,
      toplayan,
      paketleyen,
      pickingTimeMs,
      packingTimeMs,
      totalTimeMs,
      theme: getTheme(hybridProgress),
    };
  }, [task, isOpen]);
  const handleTaskUpdate = useCallback(
    async (taskId: number, payload: any) => {
      try {
        await taskStore.adminUpdate(taskId, payload);
        toast.success("İşlem başarılı.");
      } catch (e) {
        toast.error("Hata oluştu.");
      }
    },
    [taskStore],
  );
  const formatDuration = (ms: number | null) => {
    if (ms === null || isNaN(ms)) return "-";
    const totalMinutes = Math.floor(ms / 60000);
    if (totalMinutes < 1) return "< 1 dk";
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours > 0) return `${hours} sa ${mins} dk`;
    return `${mins} dk`;
  };

  // ✅ GUARD CLAUSE: Hooklardan sonra
  if (!task || !isOpen || !stats) return null;
  const getStatusInfo = (status: string) => {
    const statuses: any = {
      PENDING_PICKING: { label: "Toplama Bekliyor" },
      PICKING: { label: "Toplanıyor" },
      PICKING_PAUSED: { label: "Toplama Duraklatıldı" },
      SHORTAGE: { label: "Eksik Ürün Bildirimi" },
      PENDING_PACKAGING: { label: "Paketleme Bekliyor" },
      PACKAGING: { label: "Paketleniyor" },
      PACKAGING_PAUSED: { label: "Paketleme Duraklatıldı" },
      COMPLETED: { label: "Tamamlandı" },
      CANCELLED: { label: "İptal Edildi" },
      REVISION_NEEDED: { label: "Revizyon Gerekiyor" },
      WAITING_FOR_SUPPLY: { label: "Tedarik Bekleniyor" },
    };
    return statuses[status] || { label: status };
  };
  const config = getStatusInfo(task.status);
  const handleCopyOrderNumber = (e: any, orderNumber: string) => {
    e.stopPropagation(); // Kartın veya üst elementlerin tıklama olayını tetiklememesi için
    if (!orderNumber) return;

    navigator.clipboard.writeText(orderNumber);
    setCopied(true);

    // 2 saniye sonra ikonu eski haline getir
    setTimeout(() => setCopied(false), 2000);
  };
  const LocationBadge = ({ description }: { description?: string }) => {
    if (!description || description.trim() === "-" || description.trim() === "")
      return <span className="text-[10px] opacity-40 italic">Konum Yok</span>;
    const parts = description
      .split(" / ")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    return (
      <div className="flex flex-wrap gap-1 items-center justify-end">
        <span className="px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/30 text-[10px] font-black rounded shadow-xs uppercase tracking-tighter">
          {parts[0]}
        </span>
        {parts.slice(1).map((sub, idx) => (
          <span
            key={idx}
            className="px-1.5 py-0.5 border border-borderColor text-text text-[9px] font-bold rounded uppercase opacity-80"
          >
            {sub}
          </span>
        ))}
      </div>
    );
  };

  const handleDeleteAction = async (action: any) => {
    const actionLabel = getActionStyle(action.actionType).label;
    const confirmed = await confirmModal.confirm(
      `"${actionLabel}" işlemini silmek istediğinize emin misiniz?`,
      "İşlemi Geri Al",
    );
    if (confirmed) {
      const loadToast = toast.loading("İşlem geri alınıyor...");
      try {
        await taskStore.deleteAction(action.id);
        const updatedTask = await taskStore.getSingleTask(task.id);
        setTask(updatedTask);
        taskStore.fetchDataDynamic(taskStore.queryParams);

        toast.update(loadToast, {
          render: "İşlem silindi ve detay güncellendi.",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });
      } catch (error) {
        toast.update(loadToast, {
          render: "Hata oluştu.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  return (
    <Dialog
      className="w-[95vw] sm:w-[85vw] md:w-[75vw] lg:w-[90vw]" // Mobilde biraz daha geniş alan açtık
      header={
        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-4 pr-2 sm:pr-4">
          {/* Sol Alan - İkon ve Metinler */}
          <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0 flex-1">
            {/* Sol İkon Kutusu - Boyutları mobilde optimize edildi */}
            <div className="relative group shrink-0">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-[14px] sm:rounded-[20px] flex items-center justify-center card border-2 border-borderColor/50 shadow-xl group-hover:border-primary/50 transition-all duration-500">
                {/* İkon boyutunu da responsive yaptık */}
                <Package className="w-5 h-5 sm:w-7 sm:h-7 text-primary" />
              </div>
              {/* Durum Noktası */}
              <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-primary border-[3px] sm:border-4 border-card animate-pulse" />
            </div>

            {/* Başlık ve Detaylar Container (min-w-0 flex taşmalarını engeller) */}
            <div className="flex flex-col min-w-0 w-full">
              <div className="flex items-center flex-wrap gap-2 sm:gap-3 mb-1 sm:mb-1.5">
                {/* Sipariş No - Tıklayınca kopyalayan interaktif alan */}
                <div
                  onClick={(e) => handleCopyOrderNumber(e, task.order?.orderNumber)}
                  className="flex items-center gap-1.5 cursor-pointer group hover:opacity-80 transition-opacity max-w-full min-w-0"
                  title="Sipariş numarasını kopyala"
                >
                  <h2 className="text-sm sm:text-xl md:text-2xl lg:text-3xl font-black tracking-tighter text-text leading-none truncate">
                    #{task.order?.orderNumber}
                  </h2>

                  {/* Duruma göre değişen kopyalama ikonu (Kopyalandıysa Check, varsayılansa Copy) */}
                  <div className="shrink-0 text-text/40 group-hover:text-primary transition-colors">
                    {copied ? (
                      <Check size={16} className="text-green-500 animate-bounce-once" />
                    ) : (
                      <Copy size={16} className="opacity-0 group-hover:opacity-100 md:opacity-40 transition-opacity" />
                    )}
                  </div>
                </div>

                {/* Durum Badge - Daralmayı önlemek için shrink-0 */}
                <div className="flex items-center gap-1.5 px-2 py-0.5 sm:px-3 sm:py-1 rounded-lg sm:rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-primary whitespace-nowrap">
                    {config.label}
                  </span>
                </div>
              </div>

              {/* Alt Bilgi Satırı - Dar ekranlarda alta kırılabilecek şekilde flex-wrap eklendi */}
              <div className="flex items-center flex-wrap gap-2 sm:gap-3">
                <div className="flex items-center gap-1.5 bg-bgAlt/50 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-md sm:rounded-lg border border-borderColor/30 shrink-0">
                  <span className="text-[8px] sm:text-[9px] font-black opacity-30 uppercase tracking-tighter">
                    ID
                  </span>
                  <span className="text-[10px] sm:text-[11px] font-bold opacity-60 tabular-nums">
                    {task.id}
                  </span>
                </div>

                {task.order?.source && (
                  <>
                    <div className="hidden sm:block h-3.5 w-[1px] bg-borderColor/50" />
                    <div className="flex items-center gap-1.5 bg-purple-50 px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-md sm:rounded-lg border border-purple-200 shrink-0">
                      <span className="text-[8px] sm:text-[9px] font-black text-purple-600 uppercase tracking-tighter">
                        KAYNAK
                      </span>
                      <span className="text-[10px] sm:text-[11px] font-bold text-purple-800 uppercase">
                        {task.order.source}
                      </span>
                    </div>
                  </>
                )}

                {/* Mobilde seperator'ı gizle, yan yana geçince göster */}
                <div className="hidden sm:block h-3.5 w-[1px] bg-borderColor/50" />

                <div className="flex items-center gap-1 sm:gap-1.5 group cursor-help min-w-0">
                  <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 opacity-30 group-hover:text-primary transition-colors shrink-0" />
                  <span className="text-[9px] sm:text-[11px] font-bold opacity-40 uppercase tracking-widest group-hover:opacity-100 transition-opacity truncate">
                    {task.order?.warehouse?.name || "Depo Belirtilmemiş"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sağ Taraf İstatistik - Sadece md ve üstü ekranlarda gösterilir, UI'ı ferah tutar */}
          {task.order?.items?.length > 0 && (
            <div className="hidden md:flex flex-col items-end shrink-0 pl-4 border-l border-borderColor/20">
              <span className="text-[9px] font-black opacity-20 uppercase tracking-widest">
                Toplam Yük
              </span>
              <span className="text-xl font-black text-text/80 tracking-tighter">
                {task.order.items.length}{" "}
                <span className="text-[12px] opacity-40">Kalem</span>
              </span>
            </div>
          )}

          {task.packageCount > 0 && (
            <div className="hidden md:flex flex-col items-end shrink-0 pl-4 border-l border-borderColor/20">
              <span className="text-[9px] font-black opacity-20 uppercase tracking-widest">
                Paket Sayısı
              </span>
              <span className="text-xl font-black text-text/80 tracking-tighter">
                {task.packageCount}
              </span>
            </div>
          )}

          {/* Task Sil Butonu */}
          {onDelete && (
            <button
              onClick={async () => {
                const confirmed = await confirmModal.confirm(
                  `"#${task.order?.orderNumber}" siparişine ait iş emri silinecek. Emin misiniz?`,
                  "İş Emrini Sil"
                );
                if (confirmed) {
                  try {
                    await taskStore.deleteTask(task.id);
                    toast.success("İş emri silindi.");
                    onClose();
                    onDelete();
                  } catch {
                    toast.error("Silme sırasında hata oluştu.");
                  }
                }
              }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800/40 transition-all text-xs font-bold shrink-0"
              title="İş Emrini Sil"
            >
              <Trash2 size={14} />
              <span className="hidden sm:inline">Sil</span>
            </button>
          )}
        </div>
      }
      visible={isOpen}
      onHide={onClose}
      contentClassName="p-0"
    >
      <div className="p-2 max-w-7xl mx-auto space-y-6">
        {/* KRİTİK EKSİK PANELİ */}
        {task.status === "SHORTAGE" && (
          <div className="card border-l-4 border-l-danger p-4 flex items-center justify-between shadow-lg mb-6 animate-soft-bounce">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-danger/10 text-danger flex items-center justify-center">
                <AlertTriangle size={24} className="animate-pulse" />
              </div>
              <div>
                <div className="text-[13px] font-black tracking-tight text-danger uppercase">
                  Aktif Eksik Bildirimi
                </div>
                <div className="text-[11px] font-bold text-text opacity-70 leading-tight max-w-[250px]">
                  {/* Son giderilmemiş eksiğin açıklamasını göster */}
                  {task.shortages?.find((s: any) => !s.isResolved)
                    ?.description ||
                    "Ürün stokta bulunamadı, operasyon durduruldu."}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1">
              <div className="px-3 py-1 bg-danger text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow-sm">
                Müdahale Bekliyor
              </div>
              <span className="text-[8px] font-bold opacity-30 uppercase">
                Sistem Durduruldu
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Müşteri & Adres Bilgileri */}
          <div className="card2 p-6 flex flex-col gap-4">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest opacity-60">
              <MapPin size={14} /> Müşteri ve Adres Detayları
            </div>

            <div className="space-y-4">
              {/* Müşteri ve Telefon Bilgisi */}
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black opacity-40 uppercase">
                  Müşteri
                </span>
                <div className="text-sm font-black">
                  {task.order?.account?.name}
                </div>
                {/* Telefon Numarası Alanı */}
                <div className="flex items-center gap-1.5 text-[11px] font-bold opacity-70 mt-0.5">
                  <Phone size={12} className="text-indigo-500" />
                  <span>
                    {task.order?.account?.phone || "Telefon belirtilmemiş"}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="p-3 card2 rounded-xl border border-border">
                  <span className="text-[9px] font-black uppercase mb-1 block">
                    Sevk Adresi
                  </span>
                  <p className="text-[11px] font-bold leading-tight">
                    {task.order?.shippingAddress?.addressLine ||
                      "Adres bilgisi bulunamadı."}
                  </p>
                </div>
                <div className="p-3 card2 rounded-xl border border-border">
                  <span className="text-[9px] font-black uppercase mb-1 block">
                    Fatura Adresi
                  </span>
                  <p className="text-[11px] font-bold leading-tight">
                    {task.order?.billingAddress?.addressLine ||
                      "Adres bilgisi bulunamadı."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Personel & Operasyon Bilgi Kartı */}
          <div className="card2 p-6 flex flex-col gap-4">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest opacity-60">
              <User size={14} /> Operasyon Sorumluları
            </div>

            <div className="space-y-3">
              {/* 1. Atanan (Sorumlu) */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-[10px] border border-indigo-100 uppercase shadow-sm">
                  ATN
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] font-black opacity-40 uppercase leading-none mb-1">
                    Atanan Personel
                  </span>
                  <span className="text-xs font-bold">
                    {task.assignedUser?.name || "Atanmadı"}
                  </span>
                </div>
              </div>

              {/* 2. Toplayan */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-black text-[10px] border border-blue-100 uppercase shadow-sm">
                  TOP
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] font-black opacity-40 uppercase leading-none mb-1">
                    Toplayan
                  </span>
                  <span className="text-xs font-bold">{stats.toplayan}</span>
                </div>
              </div>

              {/* 3. Paketleyen */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-black text-[10px] border border-green-100 uppercase shadow-sm">
                  PKT
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] font-black opacity-40 uppercase leading-none mb-1">
                    Paketleyen
                  </span>
                  <span className="text-xs font-bold">{stats.paketleyen}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center bg-bgAlt border border-borderColor rounded-xl shadow-sm p-3 mt-2">
              <div className="flex flex-col items-center flex-1">
                <span className="text-[9px] font-black opacity-50 uppercase tracking-widest text-text">Toplama</span>
                <span className="text-sm font-black text-primary mt-1">{formatDuration(stats.pickingTimeMs)}</span>
              </div>
              <div className="w-px h-8 bg-borderColor/50 mx-2" />
              <div className="flex flex-col items-center flex-1">
                <span className="text-[9px] font-black opacity-50 uppercase tracking-widest text-text">Paketleme</span>
                <span className="text-sm font-black text-secondary mt-1">{formatDuration(stats.packingTimeMs)}</span>
              </div>
              <div className="w-px h-8 bg-borderColor/50 mx-2" />
              <div className="flex flex-col items-center flex-1">
                <span className="text-[9px] font-black opacity-50 uppercase tracking-widest text-text">Toplam</span>
                <span className="text-sm font-black text-success mt-1">{formatDuration(stats.totalTimeMs)}</span>
              </div>
            </div>

          </div>

          {/* İlerleme ve Not Kartı */}
          <div className="card2 p-6 flex flex-col justify-center">
            {/* 🎯 YENİ: EL TERMİNALİ HAVUZ KİLİDİ SWITCH ALANI */}
            <div className="flex items-center justify-between p-3 mb-4 rounded-xl card shadow-sm">
              <div className="flex flex-col gap-0.5 max-w-[75%]">
                <div className="flex items-center gap-1.5">
                  {task.isAssignmentRequired ? (
                    <Lock size={14} className="text-red-500 shrink-0" />
                  ) : (
                    <Unlock size={14} className="text-green-500 shrink-0" />
                  )}
                  <label htmlFor="assignmentRequired" className="font-black text-[11px] uppercase tracking-wider text-text">
                    El Terminali Havuz Kilidi
                  </label>
                </div>
                <span className="text-[10px] font-bold text-text opacity-50 leading-tight">
                  {task.isAssignmentRequired
                    ? "Bu sipariş havuzdan gizlendi. Sadece manuel atanabilir."
                    : "Sipariş genel havuzda açık. Personel sıradan çekebilir."}
                </span>
              </div>
              <InputSwitch
                inputId="assignmentRequired"
                checked={task.isAssignmentRequired || false}
                onChange={async (e) => {
                  // UI'da anlık değişim hissi yaratmak için state'i lokalde güncelle
                  setTask((prev: any) => ({ ...prev, isAssignmentRequired: e.value }));

                  // Backend'e gönder
                  await handleTaskUpdate(task.id, { isAssignmentRequired: e.value });

                  // Store'daki ana listeyi ve mevcut tekil task datasını arkada sessizce yenile
                  if (taskStore.getSingleTask) {
                    const updated = await taskStore.getSingleTask(task.id);
                    setTask(updated);
                  }
                  taskStore.fetchDataDynamic(taskStore.queryParams);
                }}
              />
            </div>
            {/* 🎯 KİLİT SWITCH ALANI BİTİŞ */}
            <div className="flex justify-between items-end mb-2">
              <div className="flex flex-col">
                <span className="text-[10px] font-black opacity-40 uppercase tracking-widest">
                  Genel İlerleme
                </span>
                <span
                  className="text-[9px] font-bold"
                  style={{ color: stats!.theme.color }}
                >
                  {stats!.theme.label}
                </span>
              </div>
              <span
                className="text-2xl font-black"
                style={{ color: stats!.theme.color }}
              >
                %{stats!.hybridProgress}
              </span>
            </div>

            <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden border border-borderColor relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/50 z-10" />
              <div
                className="h-full transition-all duration-1000 ease-out shadow-inner"
                style={{
                  width: `${stats!.hybridProgress}%`,
                  backgroundColor: stats!.theme.color,
                }}
              />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="flex flex-col items-start">
                <span className="text-[8px] opacity-50 font-bold">TOPLAMA</span>
                <span className="text-[10px] font-black">
                  {stats!.totalCollected} / {stats!.totalQty}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[8px] opacity-50 font-bold">
                  PAKETLEME
                </span>
                <span className="text-[10px] font-black">
                  {stats!.totalPacked} / {stats!.totalQty}
                </span>
              </div>
            </div>

            {/* Sipariş Notu Bölümü */}
            <div className="card p-4 mt-4 rounded-xl group">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ClipboardList size={14} />
                  <h3 className="text-[10px] font-black uppercase tracking-widest">
                    Sipariş Notu / Bilgi
                  </h3>
                </div>
                <div className="flex items-center justify-center shrink-0">
                  <IconButton
                    icon={Pencil}
                    className="p-button-text p-button-info"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTask(task);
                      setTempNote(task.order?.description || "");
                      op.current?.toggle(e);
                    }}
                  />
                </div>
              </div>

              <p className="text-[11px] font-bold leading-relaxed whitespace-pre-line">
                {task.order?.description || "Sipariş notu bulunmamaktadır."}
              </p>
            </div>


          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Ürün Listesi */}
          <div className="lg:col-span-8 space-y-4">
            <div className="flex items-center justify-between px-2">
              <h3 className="text-sm font-black flex items-center gap-2">
                <ClipboardList size={18} /> SİPARİŞ SATIRLARI
              </h3>
              <span className="text-[10px] font-bold card px-2 py-0.5 rounded opacity-70">
                {task.items?.length} Kalem
              </span>
            </div>

            <div className="card2 p-0 overflow-hidden">
              <DataTable value={stats!.physicalItems} responsiveLayout="stack">
                <Column
                  header="Görsel"
                  className="w-24"
                  body={(r) => {
                    const imageUrls = (r.stock?.images || [])
                      .filter((img: any) => img?.url && typeof img.url === "string" && img.url.trim().length > 0)
                      .map((img: any) => `${apiBase}${img.url}`);
                    const hasImages = imageUrls.length > 0;

                    return (
                      <div
                        onClick={() => {
                          if (hasImages) {
                            setGalleryState({
                              isOpen: true,
                              images: imageUrls,
                              currentIndex: 0,
                              title: r.stock?.name,
                              sku: r.stock?.sku,
                            });
                          }
                        }}
                        className={`relative group w-18 h-18 sm:w-20 sm:h-20 rounded-2xl overflow-hidden flex items-center justify-center border border-borderColor bg-card shadow-xs transition-all duration-200 ${hasImages ? 'cursor-pointer hover:border-primary hover:shadow-md hover:scale-105' : 'opacity-70'
                          }`}
                        title={hasImages ? "Görselleri büyütmek ve galeriyi açmak için tıklayın" : "Görsel yok"}
                      >
                        <SmartNetworkImage
                          imageUrls={imageUrls}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />

                        {/* Hover Overlay with Eye Icon */}
                        {hasImages && (
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white backdrop-blur-[2px]">
                            <Eye size={22} className="transform group-hover:scale-110 transition-transform drop-shadow" />
                          </div>
                        )}

                        {/* Çoklu Görsel Sayacı Rozeti */}
                        {imageUrls.length > 1 && (
                          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/75 text-white text-[9px] font-black tracking-tighter backdrop-blur-xs flex items-center gap-0.5 shadow-sm border border-white/10">
                            <ImageIcon size={9} /> {imageUrls.length}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Column
                  field="stock.name"
                  header="Ürün / Varyant / Set Bileşenleri"
                  body={(r) => {
                    const hasComponents = r.stock?.components && r.stock.components.length > 0;
                    const isPickingDone = r.collectedQty >= r.quantity;
                    const isPackagingDone = r.packedQty >= r.quantity;
                    return (
                      <div className="flex flex-col gap-2 py-1">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black tracking-tighter leading-none opacity-60">
                              {r.stock?.sku}
                            </span>
                            {hasComponents && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black bg-primary/10 text-primary border border-primary/20">
                                <Boxes size={11} /> {r.stock.components.length} Bileşenli Set
                              </span>
                            )}
                          </div>
                          <span className="text-xs font-bold line-clamp-2 mt-1 text-text">
                            {r.stock?.name}
                          </span>
                        </div>

                        {/* Set Alt Bileşenleri Listesi (Tema Uyumlu) */}
                        {hasComponents && (
                          <div className="mt-1 space-y-1.5 p-2 rounded-lg bg-bgAlt border border-borderColor">
                            <div className="flex items-center justify-between text-[10px] font-black text-text opacity-70 uppercase tracking-wider px-1">
                              <span className="flex items-center gap-1">
                                <Layers size={11} className="text-primary" /> Set Alt Bileşenleri ({r.stock.components.length})
                              </span>
                              <span className="text-[9px] opacity-60">Miktar Durumu</span>
                            </div>

                            <div className="space-y-1">
                              {r.stock.components.map((comp: any, idx: number) => {
                                const unitQty = comp.quantity && comp.quantity > 0 ? comp.quantity : 1;
                                const compRequiredQty = (r.quantity || 1) * unitQty;
                                const compCollectedQty = isPickingDone ? compRequiredQty : ((r.collectedQty || 0) * unitQty);
                                const compPackedQty = isPackagingDone ? compRequiredQty : ((r.packedQty || 0) * unitQty);
                                const isCompPickDone = compCollectedQty >= compRequiredQty;
                                const isCompPackDone = compPackedQty >= compRequiredQty;

                                return (
                                  <div
                                    key={comp.id || idx}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-card border border-borderColor shadow-xs text-xs"
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className="w-4 h-4 rounded bg-primary/10 text-primary border border-primary/20 flex items-center justify-center font-black text-[9px] shrink-0">
                                        {idx + 1}
                                      </div>
                                      <span className="font-bold text-xs text-text truncate">
                                        {comp.name}
                                      </span>
                                      {unitQty > 1 && (
                                        <span className="text-[10px] opacity-50 font-normal shrink-0">
                                          ({unitQty}x/ürün)
                                        </span>
                                      )}
                                    </div>

                                    {/* Toplanan & Paketlenen Miktar Formatı */}
                                    <div className="flex flex-wrap items-center gap-1.5 shrink-0 sm:justify-end">
                                      <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-bgAlt border border-borderColor">
                                        <span className="text-[9px] font-black uppercase opacity-60 tracking-tighter mr-0.5">
                                          T:
                                        </span>
                                        <span className={`text-xs font-black ${isCompPickDone ? "text-success" : "text-text"}`}>
                                          {formatQuantity(compCollectedQty)}
                                        </span>
                                        <span className="text-xs font-black text-text opacity-60">
                                          / {formatQuantity(compRequiredQty)}
                                        </span>
                                      </div>

                                      <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-bgAlt border border-borderColor">
                                        <span className="text-[9px] font-black uppercase opacity-60 tracking-tighter mr-0.5">
                                          P:
                                        </span>
                                        <span className={`text-xs font-black ${isCompPackDone ? "text-success" : "text-text"}`}>
                                          {formatQuantity(compPackedQty)}
                                        </span>
                                        <span className="text-xs font-black text-text opacity-60">
                                          / {formatQuantity(compRequiredQty)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Raflar - Ürün altında en altta yatayda */}
                        <div className="flex items-center gap-1.5 pt-1.5 border-t border-borderColor mt-1">
                          <span className="text-[9px] font-bold opacity-50 uppercase flex items-center gap-1 shrink-0">
                            <MapPin size={10} className="text-primary" /> Raf Konumu:
                          </span>
                          <LocationBadge description={r.stock?.description || r.stock?.shelfLocation} />
                        </div>
                      </div>
                    );
                  }}
                />
                <Column
                  header="Miktar / Durum"
                  className="w-56"
                  body={(r) => {
                    const isPickingDone = r.collectedQty >= r.quantity;
                    const isPackagingDone = r.packedQty >= r.quantity;

                    return (
                      <div className="flex flex-col gap-1.5 py-1">
                        {/* Toplanan Miktar ve Personel / Zaman */}
                        <div
                          className="flex flex-col px-2 py-1 rounded-md border border-borderColor bg-card shadow-xs transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-tighter text-text opacity-70 flex items-center gap-1">
                              <UserCheck size={11} className="text-blue-500" /> Toplanan
                            </span>
                            <div className="flex items-center gap-1.5">
                              <span className={`text-sm font-black ${isPickingDone ? "text-success" : "text-text"}`}>
                                {formatQuantity(r.collectedQty)}
                              </span>
                              <span className="text-sm font-black text-text opacity-60">
                                / {formatQuantity(r.quantity)}
                              </span>
                            </div>
                          </div>
                          {(r.pickedById || r.pickedBy || r.pickedAt) && (
                            <div className="flex items-center justify-between gap-1 text-[9px] pt-1 mt-0.5 border-t border-borderColor/50 text-text/70">
                              <span className="font-semibold truncate text-blue-600 dark:text-blue-400">
                                {resolveUserName(r.pickedById, r.pickedBy) || "Personel"}
                              </span>
                              {r.pickedAt && (
                                <span className="opacity-60 shrink-0 font-medium font-mono">
                                  {new Date(r.pickedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Paketlenen Miktar ve Personel / Zaman */}
                        <div
                          className="flex flex-col px-2 py-1 rounded-md border border-borderColor bg-card shadow-xs transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-tighter text-text opacity-70 flex items-center gap-1">
                              <PackageCheck size={11} className="text-purple-500" /> Paketlenen
                            </span>
                            <div className="flex items-center gap-1.5">
                              <span className={`text-sm font-black ${isPackagingDone ? "text-success" : "text-text"}`}>
                                {formatQuantity(r.packedQty || 0)}
                              </span>
                              <span className="text-sm font-black text-text opacity-60">
                                / {formatQuantity(r.quantity)}
                              </span>
                            </div>
                          </div>
                          {(r.packedById || r.packedBy || r.packedAt) && (
                            <div className="flex items-center justify-between gap-1 text-[9px] pt-1 mt-0.5 border-t border-borderColor/50 text-text/70">
                              <span className="font-semibold truncate text-purple-600 dark:text-purple-400">
                                {resolveUserName(r.packedById, r.packedBy) || "Personel"}
                              </span>
                              {r.packedAt && (
                                <span className="opacity-60 shrink-0 font-medium font-mono">
                                  {new Date(r.packedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                <Column
                  header="İşlemler"
                  className="w-48"
                  body={(r) => {
                    const isPickingDone = (r.collectedQty || 0) >= r.quantity;
                    const isPackagingDone = (r.packedQty || 0) >= r.quantity;
                    const hasStockError = (r.stock?.quantity ?? 0) < r.quantity;
                    const isPickLoading = processingItemId?.id === r.id && processingItemId?.action === "pick";
                    const isPackLoading = processingItemId?.id === r.id && processingItemId?.action === "pack";
                    const isUnpickLoading = processingItemId?.id === r.id && processingItemId?.action === "unpick";
                    const isUnpackLoading = processingItemId?.id === r.id && processingItemId?.action === "unpack";
                    const isAnyLoading = isPickLoading || isPackLoading || isUnpickLoading || isUnpackLoading;

                    return (
                      <div className="flex flex-col gap-1.5 py-1">
                        {/* Toplama Butonu / Durumu */}
                        {!isPickingDone ? (
                          <button
                            disabled={isAnyLoading}
                            onClick={() => handlePickItem(r)}
                            className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 hover:bg-primary text-primary hover:text-white border border-primary/20 hover:border-primary text-xs font-black transition-all shadow-xs active:scale-95 disabled:opacity-50 cursor-pointer"
                            title="Kalan tüm miktarı topla"
                          >
                            {isPickLoading ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={13} />
                            )}
                            <span>Topla ({formatQuantity(r.quantity - (r.collectedQty || 0))})</span>
                          </button>
                        ) : (
                          <div className="flex items-center justify-between w-full px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                            <span className="text-[11px] font-black flex items-center gap-1">
                              <CheckCircle2 size={13} /> Toplandı
                            </span>
                            <button
                              disabled={isAnyLoading}
                              onClick={() => handleUnpickItem(r)}
                              className="p-1 rounded hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 transition-colors cursor-pointer"
                              title="Toplamayı geri al"
                            >
                              {isUnpickLoading ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Undo2 size={12} />
                              )}
                            </button>
                          </div>
                        )}

                        {/* Paketleme Butonu / Durumu */}
                        {!isPackagingDone ? (
                          <button
                            disabled={isAnyLoading}
                            onClick={() => handlePackItem(r)}
                            className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-500/10 hover:bg-purple-600 text-purple-600 hover:text-white border border-purple-500/20 hover:border-purple-600 dark:text-purple-400 dark:hover:text-white text-xs font-black transition-all shadow-xs active:scale-95 disabled:opacity-50 cursor-pointer"
                            title="Kalan tüm miktarla paketle (eksikse otomatik toplar)"
                          >
                            {isPackLoading ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <PackageCheck size={13} />
                            )}
                            <span>Paketle ({formatQuantity(r.quantity - (r.packedQty || 0))})</span>
                          </button>
                        ) : (
                          <div className="flex items-center justify-between w-full px-2.5 py-1 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-600 dark:text-purple-400">
                            <span className="text-[11px] font-black flex items-center gap-1">
                              <PackageCheck size={13} /> Paketlendi
                            </span>
                            <button
                              disabled={isAnyLoading}
                              onClick={() => handleUnpackItem(r)}
                              className="p-1 rounded hover:bg-purple-500/20 text-purple-700 dark:text-purple-300 transition-colors cursor-pointer"
                              title="Paketlemeyi geri al"
                            >
                              {isUnpackLoading ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Undo2 size={12} />
                              )}
                            </button>
                          </div>
                        )}

                        {/* Kalan Stok - İşlemler altında en altta */}
                        <div className="flex flex-col gap-0.5 mt-0.5 border-t border-borderColor/60 pt-1">
                          <div className="flex items-center justify-between px-1">
                            <span className="text-[8px] font-bold opacity-40 uppercase">
                              Kalan Stok
                            </span>
                            <span
                              className={`text-[10px] font-medium ${hasStockError ? "text-danger font-bold" : "opacity-60"}`}
                            >
                              {formatQuantity((r.stock?.quantity ?? 0) - (r.collectedQty ?? 0))}{" "}
                              <span className="text-[8px] opacity-60 italic">
                                ADET
                              </span>
                            </span>
                          </div>
                          {hasStockError && !isPickingDone && (
                            <div className="flex items-center gap-1 text-[8px] font-black text-danger px-1 animate-pulse">
                              <AlertTriangle size={10} strokeWidth={3} /> KRİTİK: STOK YETERSİZ!
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
              </DataTable>
            </div>
          </div>

          {/* Timeline */}
          <div className="lg:col-span-4 space-y-4">
            {/* KARGO WIDGET */}
            {task?.order && (
              <CargoStatusWidget
                order={task.order}
                onUpdate={onRefresh}
              />
            )}

            <div className="flex items-center justify-between px-2">
              <h3 className="text-sm font-black flex items-center gap-2">
                <History size={18} /> İŞLEM GEÇMİŞİ
              </h3>
              <span className="text-[9px] font-bold opacity-40 uppercase tracking-widest">
                Son İşlem:{" "}
                {new Date(task.updatedAt).toLocaleTimeString("tr-TR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>

            <div className="card2   p-2 relative overflow-hidden">
              {/* Dikey Çizgi - Gradient ile daha yumuşak geçiş */}
              <div className="absolute left-[1.90rem] top-12 bottom-12 w-0.5 bg-gradient-to-b from-blue-500 via-slate-200 to-slate-100 opacity-30" />

              <div className="space-y-2">
                {task.actions && task.actions.length > 0 ? (
                  task.actions.map((action: any, idx: number) => {
                    const isFirst = idx === 0;
                    const config = getActionStyle(action.actionType);
                    const ActionIcon = config.icon;
                    const ActionColor = config.color;

                    return (
                      <div key={idx} className="relative flex gap-6 group">
                        {/* Sol İkon Alanı */}
                        <div
                          className={`
                             w-11 h-11 rounded-[14px] z-10 flex-shrink-0 flex items-center justify-center 
                             shadow-sm transition-all duration-300 border
                             ${isFirst
                              ? "bg-primary text-white border-primary scale-110 shadow-md"
                              : `card p-0 bg-green-400 border-borderColor opacity-80  text-${ActionColor}-800`
                            } 
                            `}
                        >
                          <ActionIcon size={20} strokeWidth={2.5} />

                          {/* En üstteki işlem için senin primary renginle parlama */}
                          {isFirst && (
                            <span className="absolute w-full h-full rounded-[14px] bg-primary animate-ping opacity-20" />
                          )}
                        </div>

                        {/* İçerik Alanı */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex flex-col">
                              <span
                                className={`text-[11px] font-black uppercase tracking-tight ${isFirst ? "text-primary" : "text-text"}`}
                              >
                                {config.label}
                              </span>
                              {/* Personel ve Zaman Bilgisi */}
                              <div className="flex flex-wrap items-center gap-1.5 text-[9px] font-bold text-text/60">
                                {action.user?.name && (
                                  <>
                                    <span className="text-text font-extrabold capitalize card dark:bg-surface-700 px-1.5 py-0.5 rounded-md">
                                      {action.user.name}
                                    </span>
                                    <span>•</span>
                                  </>
                                )}
                                <span>
                                  {new Date(
                                    action.createdAt,
                                  ).toLocaleDateString("tr-TR", {
                                    day: "2-digit",
                                    month: "short",
                                  })}{" "}
                                  •{" "}
                                  {new Date(
                                    action.createdAt,
                                  ).toLocaleTimeString("tr-TR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                            </div>

                            {/* Silme Butonu - Senin danger temanla */}
                            {isFirst && action.actionType !== "CREATED" && (
                              <button
                                onClick={() => handleDeleteAction(action)}
                                className="p-2 rounded-xl bg-danger/10 text-danger hover:bg-danger hover:text-white transition-all"
                                title="İşlemi Geri Al"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>

                          <p
                            className={`text-[12px] leading-relaxed mb-3 text-text ${isFirst ? "font-bold opacity-90" : "font-medium opacity-80"}`}
                          >
                            {action.description}
                          </p>

                          {/* Fotoğraf Kanıtı */}
                          {action.imageUrl && (
                            <div
                              className="relative rounded-2xl p-0 overflow-hidden border border-borderColor aspect-video group/img cursor-pointer card shadow-sm flex items-center justify-center bg-black"
                              onClick={() => {
                                setGalleryState({
                                  isOpen: true,
                                  images: [`${apiBase}${action.imageUrl}`],
                                  currentIndex: 0,
                                  title: `İşlem Kanıtı (${getActionStyle(action.actionType).label})`,
                                  sku: action.user?.name ? `Personel: ${action.user.name}` : undefined,
                                  defaultRotation: 90,
                                });
                              }}
                            >
                              <img
                                src={`${apiBase}${action.imageUrl}`}
                                className="min-w-full min-h-full object-cover transition-transform duration-500 group-hover/img:scale-125 rotate-90"
                                alt="İşlem Kanıtı"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity flex flex-col justify-end p-3">
                                <div className="flex items-center justify-between text-white">
                                  <span className="text-[10px] font-bold uppercase tracking-widest">
                                    Görseli İncele
                                  </span>
                                  <ExternalLink size={14} />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  /* İşlem Başlamadı Placeholder */
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <div className="w-16 h-16 rounded-3xl card flex items-center justify-center mb-4 border border-borderColor/50 opacity-40">
                      <History size={32} className="text-text" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-black text-text uppercase tracking-widest opacity-60">
                        Henüz İşlem Yok
                      </h4>
                      <p className="text-[11px] font-medium text-text opacity-40 max-w-[200px] leading-relaxed">
                        Bu görev için henüz bir aksiyon alınmamış. Operasyon
                        başladığında burada görünecek.
                      </p>
                    </div>

                    {/* Süreci canlandırmak için küçük bir süs (Opsiyonel) */}
                    <div className="mt-6 flex gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/20 animate-pulse" />
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/20 animate-pulse delay-75" />
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/20 animate-pulse delay-150" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <OverlayPanel
        ref={op}
        onHide={() => setEditingTask(null)}
        className="shadow-2xl border-0 overflow-hidden card2 p-0"
        style={{ width: "350px", borderRadius: "16px" }}
      >
        <div className="flex flex-col gap-3 p-4 bg-bg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-text">
              Sipariş Notu Düzenle
            </span>
            <i className="pi pi-pencil text-surface-400 text-xs" />
          </div>

          {/* InputTextarea kullanımı */}
          <InputTextarea
            value={tempNote}
            onChange={(e) => setTempNote(e.target.value)}
            rows={3} // Başlangıç yüksekliği
            autoResize // İçerik arttıkça otomatik uzar
            placeholder="Sipariş için not giriniz..."
            className="w-full p-3 text-sm border border-surface-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            onKeyDown={(e) => {
              // TextArea'da Enter alt satıra geçmeyi sağlar,
              // bu yüzden sadece Ctrl+Enter veya direkt kaydet butonuyla tetikleyebiliriz.
              if (e.ctrlKey && e.key === "Enter" && editingTask) {
                handleTaskUpdate(editingTask.id, { description: tempNote });
                op.current?.hide();
              }
            }}
            autoFocus
          />

          <div className="flex justify-end gap-2 pt-1">
            <TextButton
              onClick={() => op.current?.hide()}
              className="px-3 py-2 rounded-lg text-sm font-bold text-surface-500 hover:bg-surface-100 transition-all"
            >
              İptal
            </TextButton>
            <TextButton
              onClick={() => {
                if (editingTask) {
                  setTask({
                    ...task,
                    order: { ...task.order, description: tempNote },
                  });
                  handleTaskUpdate(editingTask.id, { description: tempNote });
                  op.current?.hide();
                }
              }}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-all shadow-md shadow-blue-500/20"
            >
              Kaydet
            </TextButton>
          </div>
        </div>
      </OverlayPanel>
      {confirmModal.modal}
      {/* Resim Detay Dialog */}
      <Dialog
        visible={!!selectedImage}
        onHide={() => setSelectedImage(null)}
        header="İşlem Kanıtı"
        className="w-[95vw] md:w-[50vw]"
        contentClassName="flex items-center justify-center bg-black/90 p-0 overflow-hidden"
        dismissableMask
      >
        {selectedImage && (
          <div className="relative flex items-center justify-center w-full h-[70vh] overflow-hidden">
            <img
              src={selectedImage}
              // Dialog içinde resmi dik gösteriyoruz
              className="max-h-[90%] w-auto rotate-90 transition-transform shadow-2xl"
              alt="Büyük Görsel"
            />

            {/* Alt Bilgi */}
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <button
                onClick={() => window.open(selectedImage, "_blank")}
                className="bg-white/10 hover:bg-white/20 text-white text-[10px] px-4 py-2 rounded-full backdrop-blur-md transition-all"
              >
                Orijinal Dosyayı Aç
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Ölçüm Dialog */}
      <Dialog
        header={measurementDialog.component ? `${measurementDialog.component.name} Ölçümü` : 'Bileşen Ölçümü'}
        visible={measurementDialog.isOpen}
        onHide={() => setMeasurementDialog(prev => ({ ...prev, isOpen: false }))}
        className="w-[90vw] md:w-[350px]"
      >
        <div className="flex flex-col gap-4 pt-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-700">Gerçekleşen Ağırlık (gr)</label>
            <InputNumber
              value={measurementDialog.weight}
              onValueChange={(e) => setMeasurementDialog(prev => ({ ...prev, weight: e.value || 0 }))}
              suffix=" gr"
              className="w-full"
              inputClassName="w-full"
            />
            <span className="text-[10px] text-gray-500">
              Not: Ağırlığı 0 yaparsanız mevcut ölçüm silinir.
            </span>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              label="İptal"
              className="p-button-text p-button-secondary py-1.5 px-3 text-xs"
              onClick={() => setMeasurementDialog(prev => ({ ...prev, isOpen: false }))}
            />
            <Button
              label="Kaydet"
              className="p-button-primary py-1.5 px-3 text-xs"
              onClick={async () => {
                try {
                  // Önce eski kayıt varsa sil
                  if (measurementDialog.currentMeasurementId) {
                    await api.delete(`/warehouse-task/items/components/${measurementDialog.currentMeasurementId}`);
                  }

                  // Eğer 0'dan büyük bir değer girildiyse yeni kayıt oluştur
                  if (measurementDialog.weight > 0) {
                    await api.post(`/warehouse-task/items/${measurementDialog.taskItem.id}/components`, {
                      stockComponentId: measurementDialog.component.id,
                      weight: measurementDialog.weight,
                      quantity: 0
                    });
                  }

                  // Task'ı yenile
                  const fullTask = await taskStore.getSingleTask(task.id, true);
                  if (fullTask) setTask(fullTask);

                  toast.success("Ölçüm başarıyla güncellendi.");
                  setMeasurementDialog(prev => ({ ...prev, isOpen: false }));
                } catch (err: any) {
                  console.error(err);
                  toast.error(err.response?.data?.message || "Ölçüm güncellenirken bir hata oluştu.");
                }
              }}
            />
          </div>
        </div>
      </Dialog>

      {/* Üst Düzey Ürün Görsel Galerisi */}
      <ImageLightbox
        isOpen={galleryState.isOpen}
        images={galleryState.images}
        currentIndex={galleryState.currentIndex}
        onIndexChange={(idx) => setGalleryState(prev => ({ ...prev, currentIndex: idx }))}
        onClose={() => setGalleryState({ isOpen: false, images: [], currentIndex: 0, title: '', sku: '', defaultRotation: 0 })}
        title={galleryState.title}
        sku={galleryState.sku}
        defaultRotation={galleryState.defaultRotation || 0}
      />

    </Dialog>
  );
};
